import React, { useCallback, useState, useContext } from 'react';
import { MdOutlineModeEditOutline } from "react-icons/md";
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ActionIcon, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { RoleService } from '../../../services/role/roleService';
import { RolesApiResponse } from '../../../type/entities/role-type';
import RolesFilters from './RolesFilters';
import { PrivilegeContext } from '../../../components/priviledge/PriviledgeProvider';

interface RolesListProps { }

const columnOrder = [
    'id',
    'name',
    'company',
    'description',
    'active',
];

const RolesList: React.FC<RolesListProps> = ({ }) => {

    // STATES
    
    const [columnNotShown, setColumnNotShown] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [changingStatus, setChangingStatus] = useState<string[]>([]);

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { handleErrors } = useHandleErrors();
    const { userCan } = useContext(PrivilegeContext);
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new RoleService();

    // FUNCTIONS
    
    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER LA LISTA DE ROLES
     * @EN GET THE LIST OF ROLES
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listRoles(filters);
        return response.getResponseData() as RolesApiResponse;
    }, [filters]));
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR EL ESTADO DEL ROL
     * @EN CHANGE THE STATUS OF THE ROLE
     */
    //--------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleRoleStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el rol" : "Se ha activado el rol");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR UN ROL
     * @EN DELETE A ROLE
     */ 
    //--------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteRole(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Rol eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR VARIOS ROLES
     * @EN DELETE MULTIPLE ROLES
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiRoles(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Roles eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTUALIZAR EL ORDEN DE LOS FILTROS
     * @EN UPDATE THE ORDER OF THE FILTERS
     */
    //--------------------------------------------------------------------------------------------------------------
    const orderedData = data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //--------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {loading
                ? <Loader height='70vh' />
                : (
                    <CustomTable
                        id={'roles-table'}
                        data={orderedData}
                        columnOrder={columnOrder}
                        isSortable
                        columnsNoSortables={[]}
                        columnsNotShown={['inmueble']}
                        overrideColumns={[
                            { 
                                key: 'name',
                                render: (row: any) => {
                                    if (userCan('get', 'roles')) {
                                        return (
                                            <a href={`${menuRoutes.roles.path}/${row.row.original.id}/edit`} className='text-primary hover:text-primary hover:underline'>
                                                {row.row.original.name}
                                            </a>
                                        );
                                    } else {
                                        return <span>{row.row.original.name}</span>;
                                    }
                                }
                            },
                            {
                                key: 'company',
                                permissions: [{ group: 'companies', permission: 'list' }],
                                render: (row: any) => {
                                    return row.row.original.company?.name || 'N/A';
                                },
                            },
                            {
                                key: 'active',
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={row.row.original.active ? 'Desactivar rol' : 'Activar rol'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <Switch
                                                id={row.row.original.id}
                                                checked={row.row.original.active}
                                                disabled={changingStatus.includes(row.row.original.id) || row.row.original.id === user?.id || !userCan('edit', 'roles')}
                                                onChange={() => toggleStatus(row.row.original.id, row.row.original.active)}
                                                switchKnobClassName='bg-red-300'
                                            />
                                        </Tooltip>
                                    );
                                },
                            },
                        ]}
                        actions={[
                            {
                                label: 'Edit',
                                permissions: { group: 'roles', permission: 'edit' },
                                render: (row: any) => {
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Editar rol'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <Link to={`${menuRoutes.roles.path}/${row.id}/edit`}>
                                                <ActionIcon
                                                    as="span"
                                                    size="sm"
                                                    variant="outline"
                                                    className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                                >
                                                    <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                                </ActionIcon>
                                            </Link>
                                        </Tooltip>
                                    );
                                },
                            },
                            {
                                label: 'Delete',
                                permissions: { group: 'roles', permission: 'delete' },
                                render: (row: any) => {
                                    if (row.id === user?.id) return null;
                                    return (
                                        <Tooltip
                                            size="sm"
                                            content={'Eliminar'}
                                            placement="top"
                                            color="invert"
                                        >
                                            <div>
                                                <DeletePopover
                                                    title={`Eliminar rol`}
                                                    description={`¿Estás seguro de que deseas eliminar a ${row.name}?`}
                                                    onDelete={() => handleDelete(row.id)}
                                                />
                                            </div>
                                        </Tooltip>
                                    );
                                },
                            },
                        ]}
                        handleMultipleDelete={handleMultiDelete}
                        filters={filters}
                        updateFilters={updateFilters}
                        updateFilterOrder={updateFilterOrder}
                        defaultOrder={filters.filter_order || undefined}
                        paginationData={{
                            pageSize: filters.limit,
                            currentPage: filters.page,
                            pageCount: (data as RolesApiResponse) ? data.lastPage : 1,
                            totalCount: data?.totalRegisters,
                            handlePagination: updatePage,
                            handlePerPage: updatePageSize,
                        }}
                        toggleFilters={() => setOpenFilters(!openFilters)}
                    />
                )}
            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Roles'}>
                <RolesFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default RolesList;